import LandingConfirmationModal from '../../components/LandingConfirmationModal';
import request from '@/services/contact';
import { initConditionalValidation, initEnterKeyPrevention, initNumericInputRestriction, initPostalCodeFormatting } from '@/utils/form-validation-helper';
import { showToast } from '@/utils/toastr';
import { FIELD_LABELS, FIELD_NAMES } from './constants';
import { initContactValidation } from './validation';

const contactPage = (() => {
    let container = null;
    let contactForm = null;
    let confirmationModal = null;

    // Collect form data
    const collectFormData = form => {
        const data = {};

        // Helper to get field value
        const getVal = name => {
            const fields = form.find(`[name="${name}"]`);
            if (!fields.length) {
                return '';
            }

            const type = String(fields.first().attr('type') || '').toLowerCase();
            const isRadioOrCheckbox = type === 'radio' || type === 'checkbox';
            const v = isRadioOrCheckbox ? fields.filter(':checked').val() : fields.val();

            return v === null || v === undefined ? '' : String(v).trim();
        };

        data[FIELD_NAMES.REQUEST_TYPE] = getVal(FIELD_NAMES.REQUEST_TYPE);
        data[FIELD_NAMES.REQUEST_DETAIL] = getVal(FIELD_NAMES.REQUEST_DETAIL);
        data[FIELD_NAMES.CUSTOMER_TYPE] = getVal(FIELD_NAMES.CUSTOMER_TYPE);
        data[FIELD_NAMES.COMPANY_NAME] = getVal(FIELD_NAMES.COMPANY_NAME);
        data[FIELD_NAMES.DEPARTMENT_NAME] = getVal(FIELD_NAMES.DEPARTMENT_NAME);
        data[FIELD_NAMES.NAME] = getVal(FIELD_NAMES.NAME);
        data[FIELD_NAMES.NAME_KANA] = getVal(FIELD_NAMES.NAME_KANA);
        data[FIELD_NAMES.EMAIL] = getVal(FIELD_NAMES.EMAIL);
        const phone1 = getVal(`${FIELD_NAMES.PHONE}1`);
        const phone2 = getVal(`${FIELD_NAMES.PHONE}2`);
        const phone3 = getVal(`${FIELD_NAMES.PHONE}3`);
        data[FIELD_NAMES.PHONE] = phone1 === '' && phone2 === '' && phone3 === '' ? '' : `${phone1}-${phone2}-${phone3}`;
        const fax1 = getVal(`${FIELD_NAMES.FAX}1`);
        const fax2 = getVal(`${FIELD_NAMES.FAX}2`);
        const fax3 = getVal(`${FIELD_NAMES.FAX}3`);
        data[FIELD_NAMES.FAX] = fax1 === '' && fax2 === '' && fax3 === '' ? '' : `${fax1}-${fax2}-${fax3}`;
        const postalCode1 = getVal(`${FIELD_NAMES.POSTAL_CODE}1`);
        const postalCode2 = getVal(`${FIELD_NAMES.POSTAL_CODE}2`);
        data[FIELD_NAMES.POSTAL_CODE] = postalCode1 === '' && postalCode2 === '' ? '' : `${postalCode1}-${postalCode2}`;
        data[FIELD_NAMES.ADDRESS] = getVal(FIELD_NAMES.ADDRESS);
        data[FIELD_NAMES.BUILDING] = getVal(FIELD_NAMES.BUILDING);
        data[FIELD_NAMES.URL_HOMEPAGE] = getVal(FIELD_NAMES.URL_HOMEPAGE);

        return data;
    };

    // Show submit confirmation modal
    const showSubmitConfirmationModal = form => {
        if (!confirmationModal) {
            confirmationModal = LandingConfirmationModal({
                title: '送信内容の確認',
                message: 'この内容で送信してもよろしいですか？',
                confirmText: '送信'
            });
        }

        // Get current form data for API submission
        const formData = collectFormData(form);

        // Generate confirmation data
        const confirmationData = [
            {
                label: FIELD_LABELS[FIELD_NAMES.REQUEST_TYPE],
                value: formData[FIELD_NAMES.REQUEST_TYPE]
            },
            {
                label: FIELD_LABELS[FIELD_NAMES.REQUEST_DETAIL],
                value: formData[FIELD_NAMES.REQUEST_DETAIL]
            },
            {
                label: FIELD_LABELS[FIELD_NAMES.CUSTOMER_TYPE],
                value: formData[FIELD_NAMES.CUSTOMER_TYPE]
            },
            {
                label: FIELD_LABELS[FIELD_NAMES.COMPANY_NAME],
                value: formData[FIELD_NAMES.COMPANY_NAME]
            },
            {
                label: FIELD_LABELS[FIELD_NAMES.DEPARTMENT_NAME],
                value: formData[FIELD_NAMES.DEPARTMENT_NAME]
            },
            {
                label: FIELD_LABELS[FIELD_NAMES.NAME],
                value: formData[FIELD_NAMES.NAME]
            },
            {
                label: FIELD_LABELS[FIELD_NAMES.NAME_KANA],
                value: formData[FIELD_NAMES.NAME_KANA]
            },
            {
                label: FIELD_LABELS[FIELD_NAMES.EMAIL],
                value: formData[FIELD_NAMES.EMAIL]
            },
            {
                label: FIELD_LABELS[FIELD_NAMES.PHONE],
                value: formData[FIELD_NAMES.PHONE]
            },
            {
                label: FIELD_LABELS[FIELD_NAMES.FAX],
                value: formData[FIELD_NAMES.FAX]
            },
            {
                label: FIELD_LABELS[FIELD_NAMES.POSTAL_CODE],
                value: formData[FIELD_NAMES.POSTAL_CODE]
            },
            {
                label: FIELD_LABELS[FIELD_NAMES.ADDRESS],
                value: formData[FIELD_NAMES.ADDRESS]
            },
            {
                label: FIELD_LABELS[FIELD_NAMES.BUILDING],
                value: formData[FIELD_NAMES.BUILDING]
            },
            {
                label: FIELD_LABELS[FIELD_NAMES.URL_HOMEPAGE],
                value: formData[FIELD_NAMES.URL_HOMEPAGE]
            }
        ];

        confirmationModal.show(
            () => submitForm(form, formData), // onConfirm
            () => confirmationModal.hide(), // onCancel
            confirmationData
        );
    };

    // Reset contact form
    const resetContactForm = form => {
        const formEl = form[0];
        if (formEl) {
            formEl.reset();
        }

        form.find('.error-message').addClass('hidden').text('');
        form.find('input, textarea, select').removeClass('error border-red-500 focus:ring-red-500 focus:border-red-500');

        if (form.data('validator')) {
            form.validate().resetForm();
        }
    };

    // Scroll to page top
    const scrollToPageTop = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // Submit form
    const submitForm = async (form, formData) => {
        const submitButton = form.find('button[type="submit"]');

        // Show loading state
        submitButton.prop('disabled', true);
        submitButton.text('送信中...');

        try {
            const response = await request.submitContact(formData);

            if (response.status === 'success') {
                showToast(response.message, 'success');
                resetContactForm(form);
                scrollToPageTop();
            }
        } catch (error) {
            console.error('Error submitting form:', error);
        } finally {
            // Reset button state
            submitButton.prop('disabled', false);
            submitButton.text('送信する');
        }
    };

    // Handle contact form submit
    const handleFormSubmit = async form => {
        await showSubmitConfirmationModal(form);
    };

    // Initialize form validation
    const initializeFormValidation = () => {
        if (contactForm.length > 0) {
            initContactValidation(contactForm, {
                errorDisplay: {
                    mode: 'field',
                    autoClearing: true,
                    onChangeValidation: true
                },
                submitHandler: async (form, event) => {
                    event.preventDefault();
                    await handleFormSubmit($(form));
                }
            });

            // Initialize conditional validation for phone fields
            initConditionalValidation(contactForm, [`${FIELD_NAMES.PHONE}1`, `${FIELD_NAMES.PHONE}2`, `${FIELD_NAMES.PHONE}3`]);

            // Initialize conditional validation for FAX fields
            initConditionalValidation(contactForm, [`${FIELD_NAMES.FAX}1`, `${FIELD_NAMES.FAX}2`, `${FIELD_NAMES.FAX}3`]);

            // Initialize conditional validation for postal code fields
            initConditionalValidation(contactForm, [`${FIELD_NAMES.POSTAL_CODE}1`, `${FIELD_NAMES.POSTAL_CODE}2`]);

            // Initialize numeric input restriction for phone/fax/postal code
            initNumericInputRestriction(contactForm, [
                `${FIELD_NAMES.PHONE}1`,
                `${FIELD_NAMES.PHONE}2`,
                `${FIELD_NAMES.PHONE}3`,
                `${FIELD_NAMES.FAX}1`,
                `${FIELD_NAMES.FAX}2`,
                `${FIELD_NAMES.FAX}3`,
                `${FIELD_NAMES.POSTAL_CODE}1`,
                `${FIELD_NAMES.POSTAL_CODE}2`
            ]);

            // Initialize postal code formatting
            initPostalCodeFormatting(contactForm, `${FIELD_NAMES.POSTAL_CODE}1`, `${FIELD_NAMES.POSTAL_CODE}2`);

            // Initialize Enter key prevention
            initEnterKeyPrevention(contactForm);
        }
    };

    // Initialize contact page
    const init = () => {
        container = $('#main-content');
        contactForm = container.find('#contact-form');
        initializeFormValidation();
    };

    return {
        init
    };
})();

// Initialize contact page
$(() => {
    contactPage.init();
});
